import React, { useState, useRef, useEffect } from 'react';
import { PlusIcon, PhotoIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import { Tooltip } from 'react-tooltip';

interface AttachmentMenuProps {
  onAddImages: () => void;
  onAddBooks: () => void;
}

export const AttachmentMenu: React.FC<AttachmentMenuProps> = ({
  onAddImages,
  onAddBooks
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleAddImages = () => {
    setIsOpen(false);
    onAddImages();
  };

  const handleAddBooks = () => {
    setIsOpen(false);
    onAddBooks();
  };

  return (
    <div className="attachment-menu-wrapper" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="attachment-button"
        aria-label="Add attachments"
        data-tooltip-id="attachment-tooltip"
        data-tooltip-content="Add images or books to your message"
      >
        <PlusIcon className="attachment-icon" />
      </button>

      {isOpen && (
        <div className="attachment-dropdown">
          <button
            onClick={handleAddImages}
            className="menu-item"
          >
            <PhotoIcon className="menu-icon" />
            <span>Add Images</span>
          </button>
          <button
            onClick={handleAddBooks}
            className="menu-item"
          >
            <BookOpenIcon className="menu-icon" />
            <span>Add Books to Context</span>
          </button>
        </div>
      )}

      <style>{`
        .attachment-menu-wrapper {
          position: relative;
        }

        .attachment-button {
          width: 36px;
          height: 36px;
          border: 2px solid #06B6D4;
          border-radius: 10px;
          background: rgba(6, 182, 212, 0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          color: #06B6D4;
        }

        .attachment-button:hover {
          background: rgba(6, 182, 212, 0.25);
          border-color: #0891B2;
        }

        .attachment-icon {
          width: 20px;
          height: 20px;
          color: #06B6D4;
        }

        .attachment-dropdown {
          position: absolute;
          bottom: 100%;
          left: 0;
          margin-bottom: 8px;
          background: #FFFFFF;
          border: 1px solid rgba(226, 232, 240, 0.8);
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          min-width: 200px;
          overflow: hidden;
          z-index: 50;
        }

        .menu-item {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: transparent;
          border: none;
          cursor: pointer;
          transition: background 0.2s;
          color: #1F2937;
          font-size: 14px;
          text-align: left;
        }

        .menu-item:hover {
          background: rgba(6, 182, 212, 0.1);
        }

        .menu-item:not(:last-child) {
          border-bottom: 1px solid rgba(226, 232, 240, 0.8);
        }

        .menu-icon {
          width: 18px;
          height: 18px;
          color: #6B7280;
        }

        /* Dark mode (already dark by default) */
        @media (prefers-color-scheme: dark) {
          .attachment-dropdown {
            background: #1f1f1f;
            border-color: #404040;
          }

          .menu-item {
            color: #e5e7eb;
          }

          .menu-icon {
            color: #9ca3af;
          }
        }

        /* Light mode - keep gradient button consistent, only style dropdown */
        @media (prefers-color-scheme: light) {
          .attachment-dropdown {
            background: white;
            border-color: #E5E7EB;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          }

          .menu-item {
            color: #1F2937;
          }

          .menu-item:hover {
            background: #F3F4F6;
          }

          .menu-item:not(:last-child) {
            border-bottom-color: #E5E7EB;
          }

          .menu-icon {
            color: #6B7280;
          }
        }

        /* Mobile */
        @media (max-width: 768px) {
          .attachment-button {
            width: 34px;
            height: 34px;
          }

          .attachment-icon {
            width: 20px;
            height: 20px;
          }

          .menu-item {
            padding: 10px 14px;
            font-size: 13px;
          }

          .menu-icon {
            width: 16px;
            height: 16px;
          }
        }

        /* Reduced motion */
        @media (prefers-reduced-motion: reduce) {
          .attachment-button,
          .menu-item {
            transition: none;
          }
        }
      `}</style>
      <Tooltip
        id="attachment-tooltip"
        style={{
          backgroundColor: '#06B6D4',
          color: '#fff',
          borderRadius: '8px',
          padding: '8px 12px',
          fontSize: '12px',
          zIndex: 9999
        }}
      />
    </div>
  );
};
